// The Q4 targets upload box's pure parts: which files it takes, and the one line that describes
// a stored upload. Never the file's contents. Kept apart from the component for `node --test`.
import type { Q4UploadReceipt, Q4StagedUpload } from '../api/types.ts';
import { formatStamp } from './format.ts';

/** Whether a picked file is a CSV, by its name: the input's `accept` is only a hint. */
export function isCsvFileName(fileName: string): boolean {
  return /\.csv$/i.test(fileName.trim());
}

/** 512 → "512 B", 88_000 → "86 KB", 2_500_000 → "2.4 MB". */
export function formatBytes(byteCount: number): string {
  if (byteCount < 1024) return `${byteCount} B`;
  if (byteCount < 1024 * 1024) return `${Math.round(byteCount / 1024)} KB`;
  return `${(byteCount / (1024 * 1024)).toFixed(1)} MB`;
}

/** The hash's first twelve characters: enough to tell two uploads apart on screen. */
export function shortSha(sha256: string): string {
  return sha256.slice(0, 12);
}

/** "#7 · q4.csv · 412 rows · 86 KB · sha256 1a2b3c4d5e6f · 4 Oct 10:03 · by a@b". The file name
 * and uploader are only on a staged upload; a fresh receipt has neither. */
export function q4UploadSummary(upload: Q4UploadReceipt | Q4StagedUpload, fileName?: string): string {
  const staged = 'file_name' in upload ? upload : null;
  const rowWord = upload.row_count === 1 ? 'row' : 'rows';
  return [
    `#${upload.upload_id}`,
    staged?.file_name ?? fileName ?? null,
    `${upload.row_count} ${rowWord}`,
    formatBytes(upload.byte_count),
    `sha256 ${shortSha(upload.sha256)}`,
    formatStamp(upload.uploaded_at),
    staged?.uploaded_by ? `by ${staged.uploaded_by}` : null,
  ].filter(Boolean).join(' · ');
}
